import React, { useMemo } from "react";
import Grid from "@mui/material/Grid2"; // שימוש ב-Grid v2
import { Box, Typography,  Paper } from "@mui/material";
import { AdjustedProductType, LiquidityPeriodBalanceType, Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { MimshakPensionService } from "../../../services/mimshakPensionService";
import { formatCurrency } from "../../../utils/namesFormater";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { MimshakService } from "../../../services/mimshakService";
import ProjectionWithoutBalanceWarning from "./ProjectionWithoutBalanceWarning";
import { PensionProductProjectionService } from "../../../services/pension/pensionProductProjection";
import { formatAdjustedProductType } from "../../../utils/enumFormatter";
import { PensionProjectionEngine } from "../../../services/pension/pensionProjectionEngine";

interface PensionSummaryCardsProps {
  products: Product[];
}

// Funds whose coefficient may stand in for a policy that reports none — in this order of preference
const FALLBACK_COEFFICIENT_TYPES = [AdjustedProductType.MANDATORY_PENSION_FUND, AdjustedProductType.SUPPLEMENTARY_PENSION_FUND];

/**
 * A coefficient for a policy without one — taken from a comprehensive pension fund, else a supplementary one,
 * preferring the same client (coefficients depend on age and gender).
 */
const findFallbackPensionCoefficient = (
  products: Product[],
  forProduct: Product,
  targetAge: number | null
): { value: number; source: string } | null => {
  const clientId = forProduct.client?.client_id;
  const candidates = products
    .filter((p) => p !== forProduct && FALLBACK_COEFFICIENT_TYPES.includes(p.details?.adjusted_product_type as AdjustedProductType))
    .sort((a, b) => {
      const sameClient = Number(b.client?.client_id === clientId) - Number(a.client?.client_id === clientId);
      if (sameClient !== 0) return sameClient;
      return (
        FALLBACK_COEFFICIENT_TYPES.indexOf(a.details?.adjusted_product_type as AdjustedProductType) -
        FALLBACK_COEFFICIENT_TYPES.indexOf(b.details?.adjusted_product_type as AdjustedProductType)
      );
    });

  for (const candidate of candidates) {
    const { coefficient } = PensionProductProjectionService.getReportedCoefficient(candidate, targetAge);
    if (coefficient && coefficient.value > 0) {
      return {
        value: coefficient.value,
        source: formatAdjustedProductType(candidate.details?.adjusted_product_type as AdjustedProductType),
      };
    }
  }
  return null;
};

const PensionSummaryCards: React.FC<PensionSummaryCardsProps> = ({ products }) => {
    // קבלת נתוני הסיכום מכל המוצרים והפוליסות
    const summaryData = useMemo(() => MimshakPensionService.aggregatePensionSummary(products), [products]);
  
    // חישובי סכומי הקצבאות וההון מחוץ לפונקציה המרכזית
    const capitalAmount = useMemo(() => MimshakService.calculateTotalFundSum(products, LiquidityPeriodBalanceType.CAPITAL), [products]);
    const nonPayingPensionAmount = useMemo(() => MimshakService.calculateTotalFundSum(products, LiquidityPeriodBalanceType.NON_PAYING_PENSION), [products]);
    const payingPensionAmount = useMemo(() => MimshakService.calculateTotalFundSum(products, LiquidityPeriodBalanceType.PAYING_PENSION), [products]);

    // התשואה מעל כל חץ — רק של הפוליסות שהשתתפו בשורה הזו, ולא של כל הפוליסות
    const payingYieldRange = useMemo(
      () =>
        MimshakPensionService.getYieldRangeOfContributingPolicies(
          products,
          (p) => (p.total_accumulated_amount_without_premiums || 0) > 0 || (p.total_accumulated_amount_with_premiums || 0) > 0
        ),
      [products]
    );
    const capitalYieldRange = useMemo(
      () =>
        MimshakPensionService.getYieldRangeOfContributingPolicies(
          products,
          (p) => (p.expected_savings_without_premiums || 0) > 0 || (p.total_expected_balance_with_premiums || 0) > 0
        ),
      [products]
    );

    // קצבה לא משלמת — היצרן לא מדווח עבורה תחזית, לכן הקצבה מחושבת במנוע של rev&u (גיל יעד לפי מגדר, 4%, מקדם מהפוליסה).
    // כשלפוליסה אין מקדם — לוקחים מקדם מקרן פנסיה מקיפה / משלימה (קודם של אותו לקוח), ומציגים שזה המקדם שנלקח
    const nonPayingEstimate = useMemo(() => {
      const projections = products
        .map((product) => ({ product, projection: PensionProductProjectionService.calculatePensionProductProjection(product) }))
        .filter(({ projection }) => projection.nonPayingAnnuity.currentBalance > 0);

      // The coefficients that actually produced a pension — the policy's own, or the one borrowed from a pension fund
      const usedCoefficients: number[] = [];
      const results = projections.map(({ product, projection }) => {
        const result = projection.nonPayingAnnuity.monthlyPensionWithoutContributions;
        if (PensionProjectionEngine.isCalculated(result)) {
          if (projection.coefficients.used) usedCoefficients.push(projection.coefficients.used.value);
          return result;
        }
        if (result.reason !== "MISSING_COEFFICIENT") return result;

        const fallback = findFallbackPensionCoefficient(products, product, projection.inputs.targetAge);
        if (!fallback) return result;
        const estimated = PensionProductProjectionService.calculatePensionProductProjection(product, {
          coefficientChoice: "estimated",
          estimatedCoefficient: fallback.value,
        }).nonPayingAnnuity.monthlyPensionWithoutContributions;
        if (PensionProjectionEngine.isCalculated(estimated)) usedCoefficients.push(fallback.value);
        return estimated;
      });

      const calculatedValues = results.filter(PensionProjectionEngine.isCalculated).map((result) => result.value);
      return {
        total: calculatedValues.reduce((sum, value) => sum + value, 0),
        any: calculatedValues.length > 0,
        missingCoefficient: results.some((result) => !PensionProjectionEngine.isCalculated(result) && result.reason === "MISSING_COEFFICIENT"),
        averageCoefficient:
          usedCoefficients.length > 0 ? usedCoefficients.reduce((sum, v) => sum + v, 0) / usedCoefficients.length : null,
        targetAges: Array.from(
          new Set(projections.map(({ projection }) => projection.inputs.targetAge).filter((age) => age != null))
        ),
      };
    }, [products]);
  
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: "right" }}>
          סיכום רכיבים פנסיוניים
        </Typography>
  
        <Grid container spacing={3} alignItems="center">
             {/* 🔹 שורה 1 - סכום  משלמת */}
             <Grid size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 2, backgroundColor: "#d4edda", color: "#155724" }}>
              <Typography variant="subtitle2">סכום נוכחי קצבה משלמת</Typography>
              <Typography variant="h6">{formatCurrency(payingPensionAmount)}</Typography>
              <ProjectionWithoutBalanceWarning products={products} />
            </Paper>
          </Grid>
         
          <Grid size={{ xs: 12, md: 1 }} sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2">{payingYieldRange}</Typography>
            <ArrowBackIcon sx={{ fontSize: 40 }} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ backgroundColor: "#cce5ff" }}>
            <Paper sx={{ p: 2, color: "#004085" }}>
              <Typography variant="subtitle1">סכום בפרישה ללא הפקדות נוספות (לצרכי חישוב הקיצבה)</Typography>
              <Typography variant="h5">{formatCurrency(summaryData.total_accumulated_amount_without_premiums)}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ backgroundColor: "#d4edda" }}>
            <Paper sx={{ p: 2, color: "#155724" }}>
              <Typography variant="subtitle1"> סכום משוער עם הפקדות עד גיל 67 (לצרכי חישוב הקיצבה)</Typography>
              <Typography variant="h5">{formatCurrency(summaryData.total_accumulated_amount_with_premiums)}</Typography>
            </Paper>
          </Grid>
          {/* 🔹 שורה 2 - קצבה משלמת */}
          <Grid size={{ xs: 12, md: 3 }}>
            {/* <Paper sx={{ p: 2, backgroundColor: "#cce5ff", color: "#004085" }}>
              <Typography variant="subtitle2">סכום נוכחי קצבה לא משלמת</Typography>
              <Typography variant="h6">{formatCurrency(nonPayingPensionAmount)}</Typography>
            </Paper> */}
          </Grid>
          <Grid size={{ xs: 12, md: 1 }} sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2">לחלק לפי מקדם</Typography>
            <ArrowBackIcon sx={{ fontSize: 40 }} />
            <Typography variant="caption" sx={{ display: "block" }}>
              {summaryData.expected_monthly_pension > 0
                ? (summaryData.total_accumulated_amount_without_premiums / summaryData.expected_monthly_pension).toFixed(1)
                : "—"}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ backgroundColor: "#cce5ff" }}>
            <Paper sx={{ p: 2, color: "#004085" }}>
              <Typography variant="subtitle1">תנתן קצבת פנסיה חודשית  לכל החיים, ללא הפקדות נוספות</Typography>
              <Typography variant="h5">{formatCurrency(summaryData.expected_monthly_pension)}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ backgroundColor: "#d4edda" }}>
            <Paper sx={{ p: 2, color: "#155724" }}>
              <Typography variant="subtitle1">תנתן קצבת פנסיה חודשית לכל החיים, בתנאי הפקדות עד גיל 67</Typography>
              <Typography variant="h5">{formatCurrency(summaryData.retirement_pension_amount)}</Typography>
            </Paper>
          </Grid>
  
         
  
          {/* 🔹 שורה 3 - קצבה לא משלמת — תמיד אחרי הקצבה המשלמת */}
          {nonPayingPensionAmount > 0 && (
            <>
              <Grid size={{ xs: 12, md: 3 }}>
                <Paper sx={{ p: 2, backgroundColor: "#cce5ff", color: "#004085" }}>
                  <Typography variant="subtitle2">סכום נוכחי קצבה לא משלמת</Typography>
                  <Typography variant="h6">{formatCurrency(nonPayingPensionAmount)}</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 1 }} sx={{ textAlign: "center" }}>
                <Typography variant="subtitle2">{nonPayingEstimate.any ? `${PensionProjectionEngine.DEFAULT_ANNUAL_RETURN * 100}%` : "-"}</Typography>
                <ArrowBackIcon sx={{ fontSize: 40 }} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} sx={{ backgroundColor: "#cce5ff" }}>
                <Paper sx={{ p: 2, color: "#004085" }}>
                  <Typography variant="subtitle1">קצבה חודשית משוערת ללא הפקדות נוספות</Typography>
                  <Typography variant="h5">
                    {nonPayingEstimate.any ? formatCurrency(nonPayingEstimate.total) : "לא חושב"}
                  </Typography>
                  {nonPayingEstimate.any && (
                    <Typography variant="caption" sx={{ display: "block" }}>
                      חישוב מקורב
                      {nonPayingEstimate.targetAges.length > 0 ? ` לגיל ${nonPayingEstimate.targetAges.join(" / ")}` : ""}
                      {nonPayingEstimate.averageCoefficient != null
                        ? ` לפי מקדם ${nonPayingEstimate.averageCoefficient.toFixed(1)}`
                        : ""}
                    </Typography>
                  )}
                  {nonPayingEstimate.missingCoefficient && (
                    <Typography variant="caption" sx={{ display: "block" }}>
                      {nonPayingEstimate.any ? "חלקי — " : ""}אין מקדם בפוליסה, לא חושבה קצבה
                    </Typography>
                  )}
                </Paper>
              </Grid>
              {/* No "with contributions" counterpart when the pension was estimated by rev&u — non-paying money gets no
                  deposits. The cell stays as an empty, transparent placeholder so the capital row still starts on its own line */}
              <Grid size={{ xs: 12, md: 4 }} sx={{ backgroundColor: nonPayingEstimate.any ? "transparent" : "#d4edda" }}>
                {!nonPayingEstimate.any && (
                  <Paper sx={{ p: 2, color: "#155724" }}>
                    <Typography variant="subtitle1">סה"כ        </Typography>
                    <Typography variant="h5">?</Typography>
                  </Paper>
                )}
              </Grid>
            </>
          )}
          {/* 🔹 שורה 4 - הון — אחרונה, רק אם יש */}
          {capitalAmount > 0 && (
            <>
              <Grid size={{ xs: 12, md: 3 }}>
                <Paper sx={{ p: 2, backgroundColor: "#d6edda", color: "#155724" }}>
                  <Typography variant="subtitle2">סכום נוכחי הוני</Typography>
                  <Typography variant="h6">{formatCurrency(capitalAmount)}</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 1 }} sx={{ textAlign: "center" }}>
                <Typography variant="subtitle2">{capitalYieldRange}</Typography>
                <ArrowBackIcon sx={{ fontSize: 40 }} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} sx={{ backgroundColor: "#cce5ff" }}>
                <Paper sx={{ p: 2, color: "#004085" }}>
                  <Typography variant="subtitle1">הון בפרישה ללא הפקדות נוספות</Typography>
                  <Typography variant="h5">{formatCurrency(summaryData.expected_savings_without_premiums)}</Typography>
                </Paper>
              </Grid>
              {/* Same amount with and without deposits — the "with deposits" cube adds nothing */}
              {summaryData.total_expected_balance_with_premiums !== summaryData.expected_savings_without_premiums && (
                <Grid size={{ xs: 12, md: 4 }} sx={{ backgroundColor: "#d4edda" }}>
                  <Paper sx={{ p: 2, color: "#155724" }}>
                    <Typography variant="subtitle1">הון בפרישה עם הפקדות עד גיל 67</Typography>
                    <Typography variant="h5">{formatCurrency(summaryData.total_expected_balance_with_premiums)}</Typography>
                  </Paper>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    );
  };
  
  export default PensionSummaryCards;